import type { Business } from '../dashboard/dashboardTypes';

export const EMAIL_TEMPLATE_AUTO_SAVE_TIMEOUT = 1000;

export enum EmailStatus {
	Waiting = 'waiting',
	Sent = 'sent',
	Open = 'open'
}

type EmailContent = {
	subject: string;
	body: string;
	templateId: string;
};

export type Email = EmailContent & {
	email?: string;
	bcc?: string;
	listId?: string;

	status?: EmailStatus;
	name?: string;
	businessId?: string;
	// eslint-disable-next-line camelcase
	mj_status?: string;
	// eslint-disable-next-line camelcase
	mj_status_count?: number;
	isScheduledEmail: boolean;
};

export type EmailTemplate = Partial<EmailContent> & {
	id?: string;
	pin?: boolean;
	name?: string;
	pinDate?: Date;
	isStale?: boolean;
	templateUsage?: number;
};

export interface EmailSuggestion {
	email: string;
	isPrimary?: boolean;
}

export enum EmailActionTypes {
	EMAIL_SEND_EMAILS_REQUEST = 'email/EMAIL_SEND_EMAILS_REQUEST',
	EMAIL_SEND_EMAILS_SUCCESS = 'email/EMAIL_SEND_EMAILS_SUCCESS',
	EMAIL_SEND_EMAILS_FAILURE = 'email/EMAIL_SEND_EMAILS_FAILURE',
	EMAIL_CLEAR_EMAILS_STATUS = 'email/EMAIL_CLEAR_EMAILS_STATUS',

	EMAIL_UPDATE_EMAIL = 'email/EMAIL_UPDATE_EMAIL',
	EMAIL_RESET_EMAIL = 'email/EMAIL_RESET_EMAIL',

	EMAIL_GET_EMAIL_TEMPLATES_REQUEST = 'email/EMAIL_GET_EMAIL_TEMPLATES_REQUEST',
	EMAIL_GET_EMAIL_TEMPLATES_SUCCESS = 'email/EMAIL_GET_EMAIL_TEMPLATES_SUCCESS',
	EMAIL_GET_EMAIL_TEMPLATES_FAILURE = 'email/EMAIL_GET_EMAIL_TEMPLATES_FAILURE',

	EMAIL_CREATE_EMAIL_TEMPLATE_REQUEST = 'email/EMAIL_CREATE_EMAIL_TEMPLATE_REQUEST',
	EMAIL_CREATE_EMAIL_TEMPLATE_SUCCESS = 'email/EMAIL_CREATE_EMAIL_TEMPLATE_SUCCESS',
	EMAIL_CREATE_EMAIL_TEMPLATE_FAILURE = 'email/EMAIL_CREATE_EMAIL_TEMPLATE_FAILURE',

	EMAIL_UPDATE_EMAIL_TEMPLATE_REQUEST = 'email/EMAIL_UPDATE_EMAIL_TEMPLATE_REQUEST',
	EMAIL_UPDATE_EMAIL_TEMPLATE_SUCCESS = 'email/EMAIL_UPDATE_EMAIL_TEMPLATE_SUCCESS',
	EMAIL_UPDATE_EMAIL_TEMPLATE_FAILURE = 'email/EMAIL_UPDATE_EMAIL_TEMPLATE_FAILURE',

	EMAIL_DELETE_EMAIL_TEMPLATE_REQUEST = 'email/EMAIL_DELETE_EMAIL_TEMPLATE_REQUEST',
	EMAIL_DELETE_EMAIL_TEMPLATE_SUCCESS = 'email/EMAIL_DELETE_EMAIL_TEMPLATE_SUCCESS',
	EMAIL_DELETE_EMAIL_TEMPLATE_FAILURE = 'email/EMAIL_DELETE_EMAIL_TEMPLATE_FAILURE',

	EMAIL_DUPLICATE_EMAIL_TEMPLATE_REQUEST = 'email/EMAIL_DUPLICATE_EMAIL_TEMPLATE_REQUEST',
	EMAIL_DUPLICATE_EMAIL_TEMPLATE_SUCCESS = 'email/EMAIL_DUPLICATE_EMAIL_TEMPLATE_SUCCESS',
	EMAIL_DUPLICATE_EMAIL_TEMPLATE_FAILURE = 'email/EMAIL_DUPLICATE_EMAIL_TEMPLATE_FAILURE',

	EMAIL_GET_EMAIL_THREAD = 'email/EMAIL_GET_EMAIL_THREAD',

	EMAIL_REPLY_MESSAGE_ID = 'email/EMAIL_REPLY_MESSAGE_ID',

	EMAIL_ADD_REPLY_SUBJECT_FOR_EMAIL = 'email/EMAIL_ADD_SUBJECT_FOR_EMAIL',
	EMAIL_REMOVE_REPLY_SUBJECT_FOR_EMAIL = 'email/EMAIL_REMOVE_REPLY_SUBJECT_FOR_EMAIL',

	EMAIL_GET_EMAIL_CONTACTED_STATUS = 'email/EMAIL_GET_EMAIL_CONTACTED_STATUS',
	EMAIL_GET_EMAIL_CONTACTED_STATUS_LOADING = 'email/EMAIL_GET_EMAIL_CONTACTED_STATUS_LOADING',
	EMAIL_GET_EMAIL_CONTACTED_STATUS_SUCCESS = 'email/EMAIL_GET_EMAIL_CONTACTED_STATUS_SUCCESS',
	EMAIL_GET_EMAIL_CONTACTED_STATUS_FAILURE = 'email/EMAIL_GET_EMAIL_CONTACTED_STATUS_FAILURE',
	EMAIL_CLEAR_EMAIL_CONTACTED_STATUS = 'email/EMAIL_CLEAR_EMAIL_CONTACTED_STATUS',

	EMAIL_GET_EMAIL_STATUSES = 'email/EMAIL_GET_EMAIL_STATUSES',
	EMAIL_GET_EMAIL_STATUSES_LOADING = 'email/EMAIL_GET_EMAIL_STATUSES_LOADING',
	EMAIL_GET_EMAIL_STATUSES_SUCCESS = 'email/EMAIL_GET_EMAIL_STATUSES_SUCCESS',
	EMAIL_GET_EMAIL_STATUSES_FAILURE = 'email/EMAIL_GET_EMAIL_STATUSES_FAILURE',
	EMAIL_CLEAR_EMAIL_STATUSES = 'email/EMAIL_CLEAR_EMAIL_STATUSES',
	EMAIL_CLEAR_EMAIL_QUEUE = 'email/EMAIL_CLEAR_EMAIL_QUEUE',

	EMAIL_GET_SCHEDULED_EMAILS = 'email/EMAIL_GET_SCHEDULED_EMAILS',
	EMAIL_GET_SCHEDULED_EMAILS_SUCCESS = 'email/EMAIL_GET_SCHEDULED_EMAILS_SUCCESS',
	EMAIL_GET_SCHEDULED_EMAILS_FAILURE = 'email/EMAIL_GET_SCHEDULED_EMAILS_FAILURE',

	EMAIL_GET_SCHEDULED_EMAILS_COUNT_BY_DATE = 'email/EMAIL_GET_SCHEDULED_EMAILS_COUNT_BY_DATE',
	EMAIL_GET_SCHEDULED_EMAILS_COUNT_BY_DATE_SUCCESS = 'email/EMAIL_GET_SCHEDULED_EMAILS_COUNT_BY_DATE_SUCCESS',

	EMAIL_GET_SCHEDULED_EMAIL_LIMITS = 'email/EMAIL_GET_SCHEDULED_EMAIL_LIMITS',
	EMAIL_GET_SCHEDULED_EMAIL_LIMITS_SUCCESS = 'email/EMAIL_GET_SCHEDULED_EMAIL_LIMITS_SUCCESS',

	EMAIL_SEND_SCHEDULED_EMAIL_SUCCESS = 'email/EMAIL_SEND_SCHEDULED_EMAIL_SUCCESS',
	EMAIL_SEND_SCHEDULED_EMAIL_FAILURE = 'email/EMAIL_SEND_SCHEDULED_EMAIL_FAILURE',
	EMAIL_SEND_SCHEDULED_EMAIL_CLEAR_FAILURE_STATUS = 'email/EMAIL_SEND_SCHEDULED_EMAIL_CLEAR_FAILURE_STATUS',

	EMAIL_CANCEL_SCHEDULED_BATCH_EMAILS_ADMIN = 'email/EMAIL_CANCEL_SCHEDULED_BATCH_EMAILS_ADMIN',
	EMAIL_CANCEL_SCHEDULED_BATCH_EMAILS_ADMIN_SUCCESS = 'EMAIL_CANCEL_SCHEDULED_BATCH_EMAILS_ADMIN_SUCCESS',
	EMAIL_CANCEL_SCHEDULED_BATCH_EMAILS_ADMIN_FAILURE = 'EMAIL_CANCEL_SCHEDULED_BATCH_EMAILS_ADMIN_FAILURE',

	EMAIL_CANCEL_SCHEDULED_BATCH_EMAILS_USER = 'email/EMAIL_CANCEL_SCHEDULED_BATCH_EMAILS_USER',
	EMAIL_CANCEL_SCHEDULED_BATCH_EMAILS_USER_SUCCESS = 'EMAIL_CANCEL_SCHEDULED_BATCH_EMAILS_USER_SUCCESS',
	EMAIL_CANCEL_SCHEDULED_BATCH_EMAILS_USER_FAILURE = 'EMAIL_CANCEL_SCHEDULED_BATCH_EMAILS_USER_FAILURE',

	EMAIL_CANCEL_SCHEDULED_BATCH_EMAILS_CLEAR_STATUS = 'email/EMAIL_CANCEL_SCHEDULED_BATCH_EMAILS_CLEAR_STATUS',

	EMAIL_CANCEL_SCHEDULED_EMAIL = 'email/EMAIL_CANCEL_SCHEDULED_EMAIL',
	EMAIL_CANCEL_SCHEDULED_EMAIL_SUCCESS = 'email/EMAIL_CANCEL_SCHEDULED_EMAIL_SUCCESS',
	EMAIL_CANCEL_SCHEDULED_EMAIL_FAILURE = 'email/EMAIL_CANCEL_SCHEDULED_EMAIL_FAILURE',
	EMAIL_CANCEL_SCHEDULED_EMAIL_CLEAR_STATUS = 'email/EMAIL_CANCEL_SCHEDULED_EMAIL_CLEAR_STATUS',
	EMAIL_GET_EMAIL_TEMPLATE_SCORES = 'email/EMAIL_GET_EMAIL_TEMPLATE_SCORES',
	EMAIL_GET_EMAIL_TEMPLATE_SCORES_SUCCESS = 'email/EMAIL_GET_EMAIL_TEMPLATE_SCORES_SUCCESS',
	EMAIL_GET_EMAIL_TEMPLATE_SCORES_FAILURE = 'email/EMAIL_GET_EMAIL_TEMPLATE_SCORES_FAILURE',

	EMAIL_GET_SCHEDULED_EMAILS_COUNT_BY_TIME_PERIOD = 'email/EMAIL_GET_SCHEDULED_EMAILS_COUNT_BY_TIME_PERIOD',
	EMAIL_GET_SCHEDULED_EMAILS_COUNT_BY_TIME_PERIOD_SUCCESS = 'email/EMAIL_GET_SCHEDULED_EMAILS_COUNT_BY_TIME_PERIOD_SUCCESS',

	EMAIL_CLEAR_EMAIL_TEMPLATE_SCORES = 'email/EMAIL_CLEAR_EMAIL_TEMPLATE_SCORES',
	EMAIL_VERIFY_EMAIL_LIMIT_INCREASE = 'email/EMAIL_VERIFY_EMAIL_LIMIT_INCREASE',

	EMAIL_INCREASE_EMAIL_TEMPLATE_USAGE = 'email/EMAIL_INCREASE_EMAIL_TEMPLATE_USAGE',
	EMAIL_CLEAR_STALE_TEMPLATE_STATE = 'email/EMAIL_CLEAR_STALE_TEMPLATE_STATE',
	EMAIL_CLEAR_UNSTALED_TEMPLATE = 'email/EMAIL_CLEAR_UNSTALED_TEMPLATE',
	EMAIL_CLEAR_LAST_STALED_TEMPLATE = 'email/EMAIL_CLEAR_LAST_STALED_TEMPLATE',

	EMAIL_SEND_ALL = 'email/EMAIL_SEND_ALL',
	EMAIL_SEND_ALL_SUCCESS = 'email/EMAIL_SEND_ALL_SUCCESS',
	EMAIL_SEND_ALL_FAILURE = 'email/EMAIL_SEND_ALL_FAILURE',
	EMAIL_SEND_ALL_CLEAR = 'email/EMAIL_SEND_ALL_CLEAR',

	EMAIL_GET_AI_SNIPPETS = 'email/EMAIL_GET_AI_SNIPPETS',
	EMAIL_GET_AI_SNIPPETS_SUCCESS = 'email/EMAIL_GET_AI_SNIPPETS_SUCCESS',
	EMAIL_CLEAR_AI_SNIPPETS_SUCCESS = 'email/EMAIL_CLEAR_AI_SNIPPETS_SUCCESS',
	EMAIL_REGENERATE_AI_SNIPPET = 'email/EMAIL_REGENERATE_AI_SNIPPET',
	EMAIL_REGENERATE_AI_SNIPPET_SUCCESS = 'email/EMAIL_REGENERATE_AI_SNIPPET_SUCCESS',
	EMAIL_REGENERATE_AI_SNIPPET_FAILURE = 'email/EMAIL_REGENERATE_AI_SNIPPET_FAILURE',
	EMAIL_GET_AI_CREDITS = 'email/EMAIL_GET_AI_CREDITS',
	EMAIL_GET_AI_CREDITS_SUCCESS = 'email/EMAIL_GET_AI_CREDITS_SUCCESS',
	EMAIL_CLEAR_AI_REGENERATE = 'email/EMAIL_CLEAR_AI_REGENERATE',
	EMAIL_DELETE_AI_SNIPPETS = 'email/EMAIL_DELETE_AI_SNIPPETS',
	EMAIL_REVERT_AI_TEMPLATE = 'email/EMAIL_REVERT_AI_TEMPLATE',
	EMAIL_CLEAR_REVERT_AI_TEMPLATE = 'email/EMAIL_CLEAR_REVERT_AI_TEMPLATE',

	EMAIL_SEND_CUSTOMER_REPORT_BAD_DATA = 'email/EMAIL_SEND_CUSTOMER_REPORT_BAD_DATA',
	EMAIL_SEND_CUSTOMER_REPORT_BAD_DATA_SUCCESS = 'email/EMAIL_SEND_CUSTOMER_REPORT_BAD_DATA_SUCCESS',
	EMAIL_SEND_CUSTOMER_REPORT_BAD_DATA_FAILURE = 'email/EMAIL_SEND_CUSTOMER_REPORT_BAD_DATA_FAILURE',
	EMAIL_SEND_CUSTOMER_REPORT_BAD_DATA_CLEAR = 'email/EMAIL_SEND_CUSTOMER_REPORT_BAD_DATA_CLEAR',

	EMAIL_GET_MONTHLY_EMAIL_LIMIT = 'email/EMAIL_GET_MONTHLY_EMAIL_LIMIT',
	EMAIL_GET_MONTHLY_EMAIL_LIMIT_SUCCESS = 'email/EMAIL_GET_MONTHLY_EMAIL_LIMIT_SUCCESS'
}

export type DirectEmail = {
	business: Business;
	email: string;
};

export interface EmailAction {
	type: EmailActionTypes;
	payload?: any;
	error?: Error;
}
